import { Action, Location } from 'history';
import history from 'history/browser';
import { useCallback, useEffect, useRef } from 'react';
import useHistory from '../Router/useHistory';
import Events from '../utils/events';

const EV_HASH_CHANGE = 'EV_HASH_CHANGE';

const _history = history || {};

interface IHashState {
  prevHash: string;
  hash: string;
  hashChange: boolean;
}

const hashState: IHashState = {
  prevHash: '',
  hash: SSR ? '' : location?.hash || '',
  hashChange: false,
};

export default () => {
  const stack = useRef<Location[]>([]);

  useEffect(() => {
    stack.current.push({ ...location, state: undefined, key: 'default' });

    _history.listen(({ location, action }) => {
      const prevLocation = stack.current[stack.current.length - 1];

      const hashAddedInUrl = !!location.hash && location.hash !== prevLocation?.hash;

      if (hashAddedInUrl) {
        stack.current.push(location);
      } else {
        switch (action) {
          case Action.Pop:
            stack.current.pop();
            break;

          case Action.Push:
            stack.current.push(location);
            break;

          case Action.Replace:
            stack.current[Math.max(0, stack.current.length - 1)] = location;
            break;

          default:
            break;
        }
      }

      if (!prevLocation) {
        return;
      }

      const samePath = prevLocation.pathname === location.pathname;
      const sameQueryString = prevLocation.search === location.search;
      const hashDiff = prevLocation.hash !== location.hash;

      if (samePath && sameQueryString && hashDiff) {
        hashState.hashChange = true;
        Events.emit(EV_HASH_CHANGE, prevLocation.hash, location.hash);
      } else {
        hashState.hashChange = false;
      }

      hashState.hash = location.hash;
      hashState.prevHash = prevLocation.hash;
    });
  }, []);

  return hashState;
};

export const useOnHash = (hash: string, callback: (prevHash: string, hash: string) => void) => {
  const { replace, location } = useHistory();

  useEffect(() => {
    Events.on(EV_HASH_CHANGE, (prevHash: string, nextHash: string) => {
      if (nextHash === hash || prevHash === hash) {
        callback(prevHash, nextHash);
      }
    });

    return () => {
      Events.off(EV_HASH_CHANGE, callback);
    };
  }, [hash, callback]);

  return useCallback(() => {
    if (location?.hash === hash) {
      replace({ hash: '' });
    }
  }, [location?.hash, hash]);
};
